import { DataResult, SortDescriptor, State } from '@progress/kendo-data-query';
import {
  GridCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import { produce } from 'immer';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { DataLoader } from '../../../../../../common/widgets/DataLoader';
import Grid, { GridCellTd } from '../../../../../../common/widgets/Grid';
import Loader from '../../../../../../common/widgets/Loader';
import { config } from '../../../../config';
import { ClientOrderProductItemModel } from '../../../../models/ClientOrders';
import { StatisticsFilterModel } from '../../../../models/StatisticsFilterModel';
import { resources } from '../../../../resources/resources';
import { clientOrdersService } from '../../../../services/clientOrders';
import { getFilter } from '../helper';

export interface ClientOrderProdutsState {
  orderProducts: {
    data: ClientOrderProductItemModel[];
    dataState: State;
    total: number;
  };
}
interface ClientOrderProductsProps {
  filters: StatisticsFilterModel;
}

export default function ClientOrderProducts(
  props: Readonly<ClientOrderProductsProps>,
) {
  const { t: translate } = useTranslation();
  const initialDataState: State = {
    skip: 0,
    take: config.grid.defaultPageSize,
    sort: [{ field: 'dateAdded', dir: 'desc' }],
    filter: getFilter(props.filters),
  };
  const [state, setState] = useState<ClientOrderProdutsState>({
    orderProducts: {
      data: [] as ClientOrderProductItemModel[],
      dataState: initialDataState,
    },
  } as ClientOrderProdutsState);

  const onDataReceived = (result: DataResult) => {
    setState(
      produce(draft => {
        draft.orderProducts.data = result.data;
        draft.orderProducts.total = result.total;
      }),
    );
  };

  const getSort = (sort?: SortDescriptor[]) => {
    if (sort && sort.length > 1 && _.some(sort, ['field', 'dateAdded'])) {
      return _.filter(sort, (s: SortDescriptor) => {
        return s.field !== 'dateAdded';
      });
    } else if (sort && sort.length === 0) {
      return [{ field: 'dateAdded', dir: 'desc' }] as SortDescriptor[];
    } else {
      return sort;
    }
  };

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setState(
      produce(draft => {
        draft.orderProducts.dataState = {
          ...e.dataState,
          sort: getSort(e.dataState.sort),
        };
      }),
    );
  };

  useEffect(() => {
    setState(
      produce(d => {
        d.orderProducts.dataState.filter = getFilter(props.filters);
      }),
    );
  }, [props.filters]);

  return (
    <div className="panel padding-xl admin-statistics-section">
      <h2 className="statistics-subtitle">
        {translate(resources.admin.statistics.topValueInformation)}
      </h2>
      <DataLoader
        getData={clientOrdersService.getOrders}
        dataState={state.orderProducts.dataState}
        onDataReceived={onDataReceived}
      />
      <Loader loading={!state.orderProducts.data?.length} loader={<Skeleton />}>
        <div className="admin-grid statistics-info-grid">
          <Grid
            data={state.orderProducts.data}
            pageable={{
              pageSizes: config.grid.pageSizes,
              pageSizeValue: state.orderProducts.dataState.take,
              info: false,
              previousNext: true,
            }}
            {...state.orderProducts.dataState}
            onDataStateChange={e => onDataStateChange(e)}
            total={state.orderProducts.total}
            sortable={{ allowUnsort: true, mode: 'multiple' }}
            sort={state.orderProducts.dataState.sort}
            columns={[
              {
                title: translate(resources.admin.statistics.benefitName),
                field: 'productName',
                sortable: true,
                cell: productNameCell,
              },
              {
                title: translate(resources.admin.statistics.employeeName),
                field: 'employeeName',
                sortable: true,
                cell: employeeNameCell,
              },
              {
                title: translate(
                  resources.admin.statistics.employeeContribution,
                ),
                field: 'companyContribution',
                sortable: true,
                cell: companyContributionCell,
                headerClassName: 'grid-number break-title',
              },
              {
                title: translate(
                  resources.admin.statistics.personalContribution,
                ),
                field: 'personalContribution',
                sortable: true,
                cell: personalContributionCell,
                headerClassName: 'grid-number break-title',
              },
              {
                title: translate(resources.admin.statistics.total),
                field: 'total',
                sortable: true,
                cell: totalCell,
                headerClassName: 'grid-number',
              },
            ]}
          />
        </div>
      </Loader>
    </div>
  );
}

const productNameCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd className="admin-grid-cell-highlight">
    {dataItem.productName}
  </GridCellTd>
);

const employeeNameCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{dataItem.employeeName}</GridCellTd>
);

const companyContributionCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd className="grid-number">
    {dataItem.companyContribution} {config.currency}
  </GridCellTd>
);

const personalContributionCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd className="grid-number">
    {dataItem.personalContribution} {config.currency}
  </GridCellTd>
);

const totalCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd className="grid-number admin-grid-cell-highlight">
    {dataItem.total} {config.currency}
  </GridCellTd>
);
